import { useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { Result } from "neverthrow";
import { err, errAsync, ok, okAsync, ResultAsync } from "neverthrow";

import type {
  ConflictError,
  InternalError,
  InvalidError,
  NotFoundError,
} from "@thalamos/common";
import { buildInternalError, buildInvalidError } from "@thalamos/common";
import {
  Base64FileUpload,
  base64PayloadOf,
  decodeBase64,
} from "@thalamos/frontend";
import type { UseSingleUseActionTokenResponse } from "@thalamos/aspire-common";

import type { BulkEmailMigrationActionProps } from "../../types.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  renderErrorToast,
} from "~/components/design-system/index.js";

const laneFor = (
  token: BulkEmailMigrationActionProps["singleUseActionToken"],
): "user" | "team" =>
  token.type === "bulkUserEmailMigration" ? "user" : "team";

export function BulkEmailMigrationActionInner({
  singleUseActionToken,
  uploadCsvFn,
  downloadTemplate,
}: BulkEmailMigrationActionProps) {
  const lane = laneFor(singleUseActionToken);

  const [validatedFile, setValidatedFile] = useState<string | null>(null);
  const [dryRunResult, setDryRunResult] = useState<Result<
    UseSingleUseActionTokenResponse,
    InvalidError | NotFoundError | ConflictError | InternalError
  > | null>(null);
  const [executeResult, setExecuteResult] = useState<Result<
    UseSingleUseActionTokenResponse,
    InvalidError | NotFoundError | ConflictError | InternalError
  > | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const migrated = executeResult?.isOk() ?? false;

  const handleDownloadTemplate = useCallback(async () => {
    setIsProcessing(true);
    try {
      await downloadTemplate();
    } catch {
      renderErrorToast({ message: "Failed to download template" });
    } finally {
      setIsProcessing(false);
    }
  }, [downloadTemplate]);

  const handleFileUpload = useCallback(
    (files: { base64: string | ArrayBuffer }[]) => {
      setValidatedFile(null);
      setDryRunResult(null);
      setExecuteResult(null);
      setIsProcessing(true);

      const uploadedFile = files.length === 1 ? files[0] : undefined;
      if (uploadedFile === undefined) {
        renderErrorToast({ message: "Please upload precisely one CSV file" });
        setIsProcessing(false);
        return;
      }

      const base64Value = uploadedFile.base64;
      if (typeof base64Value !== "string") {
        renderErrorToast({
          message: "File could not be read as base64 string",
        });
        setIsProcessing(false);
        return;
      }

      const csvData = decodeBase64(base64PayloadOf(base64Value));

      // Uploading only validates (dryRun=true); the Migrate button performs the
      // execute (dryRun=false) on the validated file.
      void uploadCsvFn(singleUseActionToken.id, csvData, true).match(
        (okValue) => {
          setDryRunResult(ok(okValue));
          setValidatedFile(csvData);
          setIsProcessing(false);
        },
        (error) => {
          setDryRunResult(err(error));
          renderErrorToast({ message: error.message });
          setIsProcessing(false);
        },
      );
    },
    [singleUseActionToken.id, uploadCsvFn],
  );

  const handleExecute = useCallback(() => {
    if (validatedFile === null) {
      return;
    }
    setExecuteResult(null);
    setIsProcessing(true);

    // Execute with dryRun=false. The server re-validates and swaps atomically,
    // so a failure here rolls the whole lane back and leaves the token unused —
    // the same file can be corrected and retried.
    void uploadCsvFn(singleUseActionToken.id, validatedFile, false).match(
      (okValue) => {
        setExecuteResult(ok(okValue));
        setIsProcessing(false);
      },
      (error) => {
        setExecuteResult(err(error));
        renderErrorToast({ message: error.message });
        setIsProcessing(false);
      },
    );
  }, [singleUseActionToken.id, uploadCsvFn, validatedFile]);

  return (
    <Box data-testid="use-valid-token">
      <Typography variant="h4">Bulk migrate {lane} email addresses</Typography>
      <Typography>
        Download the template for this organisation, fill in the new email
        address for each row, then upload it to validate the changes.
      </Typography>

      <Box sx={{ my: 2 }}>
        <Button
          label="Download template"
          variant="outlined"
          disabled={isProcessing}
          testId="download-template-button"
          onClick={() => {
            void handleDownloadTemplate();
          }}
        />
      </Box>

      <Box sx={{ my: 2 }}>
        <Base64FileUpload
          disabled={isProcessing}
          label="Upload CSV"
          // Include the MIME types, not just ".csv": macOS matches the native
          // file dialog on the file's registered type, so an extension-only
          // filter greys out CSVs registered to Excel/Numbers.
          accept=".csv,text/csv,application/vnd.ms-excel"
          // A CSV is never a camera capture; force the plain file picker rather
          // than the component's camera default.
          uploadSource="cameraOrFiles"
          onDone={handleFileUpload}
        />
      </Box>

      {dryRunResult?.isErr() && (
        <Box data-testid="file-invalid-banner">
          <Banner
            title="File validation failed"
            body={[
              dryRunResult.error.message,
              ...(dryRunResult.error.issues ?? []),
            ]}
            bannerType={BannerList.ERROR}
          />
        </Box>
      )}

      {executeResult?.isErr() && (
        <Box sx={{ mb: 2 }} data-testid="migration-failed-banner">
          <Banner
            title="Migration failed — no changes were made"
            body={[
              executeResult.error.message,
              ...(executeResult.error.issues ?? []),
            ]}
            bannerType={BannerList.ERROR}
          />
        </Box>
      )}

      {migrated && executeResult?.isOk() && (
        <Box sx={{ mb: 2 }} data-testid="migration-complete-banner">
          <Banner
            title={`Migration complete — ${String(
              executeResult.value.type === "bulkTeamEmailMigration" ||
                executeResult.value.type === "bulkUserEmailMigration"
                ? (executeResult.value.data?.migratedCount ?? 0)
                : 0,
            )} ${lane} email address(es) updated. This token has now been used.`}
            bannerType={BannerList.CONFIRMATION}
          />
        </Box>
      )}

      {validatedFile && dryRunResult?.isOk() && !migrated && (
        <>
          {/* Suppress the green "validated" banner once an execute attempt has
              failed: the failure (typically a collision found at re-validation)
              means the file is no longer valid against current state, so the
              red failure banner is the accurate message. The Migrate button
              stays below for a retry. */}
          {!executeResult?.isErr() && (
            <Box sx={{ mb: 2 }} data-testid="file-validated">
              <Banner
                title="File validated. Review the rows above, then migrate the email addresses."
                bannerType={BannerList.CONFIRMATION}
              />
            </Box>
          )}

          <Button
            label="Migrate emails"
            disabled={isProcessing}
            testId="migrate-emails-button"
            onClick={handleExecute}
          />
        </>
      )}
    </Box>
  );
}

export function BulkEmailMigrationAction({
  token,
}: {
  token: BulkEmailMigrationActionProps["singleUseActionToken"];
}) {
  const lane = laneFor(token);

  const uploadCsvFn = useCallback(
    (
      tokenId: string,
      csvData: string,
      dryRun: boolean,
    ): ResultAsync<
      UseSingleUseActionTokenResponse,
      InvalidError | NotFoundError | ConflictError | InternalError
    > => {
      return ResultAsync.fromPromise(
        api.singleUseActionToken.use(tokenId, csvData, dryRun),
        (e) =>
          buildInternalError({
            message: e instanceof Error ? e.message : String(e),
          }),
      ).andThen((response) => {
        if (response.status === 200) {
          // The 200 body is the lane-tagged union member (the migration report
          // on execute, the bare member on dry-run). A missing/typeless body
          // means a broken contract, so treat it as an internal error rather
          // than a silent success.
          if (!response.data?.type) {
            return errAsync(
              buildInternalError({
                message:
                  "Server returned an empty response for a successful token action",
              }),
            );
          }
          return okAsync(response.data);
        } else if (response.status === 400) {
          const data = response.data as unknown as {
            message?: string;
            issues?: string[];
          };
          const message =
            data?.message ?? "Unknown error - Please contact Thalamos";
          // The dry-run reports per-row validation failures via `issues`;
          // preserve them so the banner can list each failing row.
          return errAsync(buildInvalidError({ message, issues: data?.issues }));
        } else {
          // Non-validation failures (5xx, transport) are server errors, not
          // per-row validation — map to InternalError without `issues`.
          const data = response.data as unknown as { message?: string };
          const message =
            data?.message ?? "Unknown error - Please contact Thalamos";
          return errAsync(buildInternalError({ message }));
        }
      });
    },
    [lane],
  );

  const downloadTemplate = useCallback(async () => {
    const result = await api.singleUseActionToken.downloadTemplate(token.id);
    if (result.status !== 200) {
      renderErrorToast({ message: "Failed to download template" });
      return;
    }

    const url = window.URL.createObjectURL(
      new Blob([result.data], { type: "text/csv" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${lane}-email-migration-template.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, [token.id, lane]);

  return (
    <BulkEmailMigrationActionInner
      singleUseActionToken={token}
      uploadCsvFn={uploadCsvFn}
      downloadTemplate={downloadTemplate}
    />
  );
}
